import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ApiError, getJSON } from '@/lib/api'
import { hasFieldErrors } from '@/lib/contacts/errors'
import { cn } from '@/lib/utils'
import { DetailField } from './event-details-fields'
import type { PreviousEdition } from './previous-edition-types'

interface FieldProps {
  eventId: number
  startsAt: string | null
  value: PreviousEdition | null
  onChange: (value: PreviousEdition | null) => void
  error?: string
  readOnly?: boolean
  pending?: boolean
}

export function previousEditionError(error: unknown) {
  if (!(error instanceof ApiError) || !hasFieldErrors(error.body)) return undefined
  return error.body.errors.previous_edition_id?.join(' ')
}

function EventValue({
  event,
  option = false,
}: {
  event: PreviousEdition | null
  option?: boolean
}) {
  if (!event) return <span className="text-muted-foreground">None</span>
  return (
    <>
      <span className="truncate text-foreground">{event.name}</span>
      <span className={cn('shrink-0 text-caption text-muted-foreground', option && 'ml-auto')}>
        {formatDate(event.starts_on)}
      </span>
    </>
  )
}

function formatDate(date: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`))
}

export function PreviousEditionField({
  eventId,
  startsAt,
  value,
  onChange,
  error,
  readOnly = false,
  pending = false,
}: FieldProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const disabled = !startsAt || pending
  const query = useQuery({
    queryKey: ['previous-edition-candidates', eventId, startsAt, search],
    queryFn: () =>
      getJSON<{ events: PreviousEdition[] }>(
        `/api/v1/events?${new URLSearchParams({ previous_edition_for: String(eventId), search, starts_at: startsAt ?? '' })}`,
      ),
    enabled: open && !disabled && !readOnly,
    retry: false,
  })
  if (readOnly)
    return (
      <DetailField label="Previous edition">
        <div className="flex items-center gap-2 py-1.5 text-body-sm">
          <EventValue event={value} />
        </div>
      </DetailField>
    )
  const caption =
    error ??
    (!startsAt
      ? 'Set a start date to choose a previous edition.'
      : 'Compares registrations with this event.')
  return (
    <DetailField label="Previous edition" htmlFor="previous-edition">
      <div className="space-y-1.5">
        <Popover open={open && !disabled} onOpenChange={setOpen}>
          <PreviousEditionControl
            value={value}
            error={error}
            disabled={disabled}
            open={open}
            onClear={() => onChange(null)}
          />
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] min-w-72 p-0"
          >
            <Command shouldFilter={false}>
              <CommandInput placeholder="Search events" value={search} onValueChange={setSearch} />
              <CandidateList
                pending={query.isPending}
                error={query.error}
                events={query.data?.events ?? []}
                onSelect={(event) => {
                  onChange(event)
                  setOpen(false)
                }}
              />
            </Command>
          </PopoverContent>
        </Popover>
        <p
          id="previous-edition-caption"
          role={error ? 'alert' : undefined}
          className={cn('text-caption', error ? 'text-destructive' : 'text-muted-foreground')}
        >
          {caption}
        </p>
      </div>
    </DetailField>
  )
}

function PreviousEditionControl({
  value,
  error,
  disabled,
  open,
  onClear,
}: {
  value: PreviousEdition | null
  error?: string
  disabled: boolean
  open: boolean
  onClear: () => void
}) {
  return (
    <PopoverAnchor asChild>
      <div
        className={cn(
          'relative flex h-9 w-full items-center rounded-md border bg-popover',
          error ? 'border-destructive' : 'border-input',
          disabled && 'opacity-50',
        )}
      >
        <PopoverTrigger asChild>
          <Button
            id="previous-edition"
            type="button"
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            aria-invalid={Boolean(error)}
            aria-describedby="previous-edition-caption"
            disabled={disabled}
            className={cn(
              'h-full min-w-0 flex-1 justify-start gap-2 px-3 text-left text-body-sm font-normal',
              value ? 'pr-16' : 'pr-9',
            )}
          >
            <EventValue event={value} />
            <ChevronDown
              className="absolute right-3 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        {value && !disabled && (
          <Button
            type="button"
            variant="ghost"
            className="absolute right-8 h-6 w-6 p-0 text-muted-foreground"
            aria-label="Clear previous edition"
            onClick={onClear}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        )}
      </div>
    </PopoverAnchor>
  )
}

function CandidateList({
  pending,
  error,
  events,
  onSelect,
}: {
  pending: boolean
  error: Error | null
  events: PreviousEdition[]
  onSelect: (event: PreviousEdition) => void
}) {
  return (
    <CommandList>
      {pending ? (
        <p role="status" className="p-3 text-caption text-muted-foreground">
          Loading events…
        </p>
      ) : error ? (
        <p role="alert" className="p-3 text-caption text-destructive">
          {error.message}
        </p>
      ) : (
        <>
          <CommandEmpty>No earlier events found</CommandEmpty>
          <CommandGroup>
            {events.map((event) => (
              <CommandItem key={event.id} value={String(event.id)} onSelect={() => onSelect(event)}>
                <EventValue event={event} option />
              </CommandItem>
            ))}
          </CommandGroup>
        </>
      )}
    </CommandList>
  )
}
